import { useState } from 'react';
import { Newspaper, ChevronDown, Check } from 'lucide-react';

interface NewsItemData {
  id: string;
  title: string;
  source?: string;
  summary?: string;
  why_it_matters?: string;
  action_tag?: string;
  read?: boolean;
  published_at?: string;
  created_at?: string;
}

const TAG_STYLE: Record<string, { bg: string; text: string }> = {
  watch:       { bg: 'rgba(110,168,255,0.12)', text: 'var(--sky)' },
  discuss:     { bg: 'rgba(140,120,200,0.12)', text: '#8C78C8' },
  'act now':   { bg: 'rgba(226,85,85,0.10)',   text: 'var(--danger)' },
  act_now:     { bg: 'rgba(226,85,85,0.10)',   text: 'var(--danger)' },
  opportunity: { bg: 'rgba(31,157,110,0.10)',   text: 'var(--success)' },
  risk:        { bg: 'rgba(226,85,85,0.10)',    text: 'var(--danger)' },
};

function normalizeTag(tag?: string): string {
  return (tag || '').toLowerCase().replace(/_/g, ' ');
}

function timeAgo(ts?: string): string {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function TagBadge({ tag }: { tag?: string }) {
  if (!tag) return null;
  const normalized = normalizeTag(tag);
  const style = TAG_STYLE[normalized] || { bg: 'rgba(20,20,40,0.05)', text: 'var(--muted)' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
      background: style.bg, color: style.text, flexShrink: 0, textTransform: 'capitalize',
    }}>
      {tag.replace(/_/g, ' ')}
    </span>
  );
}

export default function NewsCard({ data, fetching }: { data?: NewsItemData[] | null; fetching?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [readItems, setReadItems] = useState<Set<string>>(new Set());

  if (!data || data.length === 0) {
    return (
      <div style={{ marginTop: 24 }}>
        <div className="lumen-glass lumen-spring" style={{ borderRadius: 20, padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Newspaper size={16} style={{ color: 'var(--muted)' }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>News & Intel</span>
            {fetching && (
              <div style={{ display: 'flex', gap: 4, marginLeft: 6 }}>
                <div className="lumen-orb" style={{ width: 5, height: 5 }} />
                <div className="lumen-orb" style={{ width: 5, height: 5, animationDelay: '0.15s' }} />
                <div className="lumen-orb" style={{ width: 5, height: 5, animationDelay: '0.3s' }} />
              </div>
            )}
          </div>
          {fetching ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ height: 10, background: 'var(--border)', borderRadius: 4, width: '100%', marginBottom: 8, opacity: 0.6 }} />
              <div style={{ height: 10, background: 'var(--border)', borderRadius: 4, width: '80%', marginBottom: 8, opacity: 0.4 }} />
              <div style={{ height: 10, background: 'var(--border)', borderRadius: 4, width: '60%', opacity: 0.3 }} />
              <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>Fetching latest news...</p>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '8px 0 4px' }}>No relevant news</p>
          )}
        </div>
      </div>
    );
  }

  const toggleItem = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const markAsRead = (id: string) => {
    setReadItems(prev => new Set(prev).add(id));
  };

  const unreadCount = data.filter(n => !n.read && !readItems.has(n.id)).length;
  const topItem = data[0];
  const topAge = timeAgo(topItem?.published_at || topItem?.created_at);

  return (
    <div style={{ marginTop: 24 }}>
      <div className="lumen-glass lumen-spring" style={{ borderRadius: 20, padding: '16px 18px' }}>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Newspaper size={16} style={{ color: 'var(--muted)' }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>News & Intel</span>
            {unreadCount > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6,
                background: 'var(--gold-dim)', color: 'var(--gold)',
              }}>
                {unreadCount}
              </span>
            )}
          </div>
          <ChevronDown
            size={16}
            style={{
              color: 'var(--muted)',
              transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1)',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            }}
          />
        </button>

        {!expanded && topItem && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <p style={{ fontSize: 13, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{topItem.title}</p>
              <TagBadge tag={topItem.action_tag} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              {topItem.why_it_matters && (
                <p style={{ fontSize: 12, color: 'var(--muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{topItem.why_it_matters}</p>
              )}
              {topAge && <span className="font-mono-tabular" style={{ fontSize: 10, color: 'var(--subtle)', flexShrink: 0 }}>{topAge}</span>}
            </div>
          </div>
        )}

        {expanded && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.map(item => {
              const isRead = item.read || readItems.has(item.id);
              const isExpanded = expandedItems.has(item.id);
              const age = timeAgo(item.published_at || item.created_at);
              return (
                <div
                  key={item.id}
                  style={{
                    borderRadius: 12, overflow: 'hidden',
                    background: isRead ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)',
                    border: '1px solid var(--border)',
                    opacity: isRead ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <button
                    onClick={() => toggleItem(item.id)}
                    style={{
                      width: '100%', padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 8,
                      textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, color: 'var(--text)', margin: 0 }}>{item.title}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                        {item.source && <span style={{ fontSize: 11, color: 'var(--muted)' }}>{item.source}</span>}
                        {age && <span className="font-mono-tabular" style={{ fontSize: 10, color: 'var(--subtle)' }}>{age}</span>}
                      </div>
                    </div>
                    <TagBadge tag={item.action_tag} />
                  </button>

                  {isExpanded && (
                    <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {item.why_it_matters && (
                        <div style={{
                          background: 'var(--gold-dim)', borderRadius: 10, padding: '8px 10px',
                          border: '1px solid rgba(232,155,48,0.15)',
                        }}>
                          <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Why it matters</p>
                          <p style={{ fontSize: 12, color: 'var(--text)', marginTop: 4, lineHeight: 1.5, margin: '4px 0 0' }}>{item.why_it_matters}</p>
                        </div>
                      )}
                      {item.summary && (
                        <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: 0 }}>{item.summary}</p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {!isRead && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAsRead(item.id); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                              color: 'var(--muted)', background: 'none', border: 'none',
                              cursor: 'pointer', fontFamily: 'inherit', padding: 0,
                            }}
                          >
                            <Check size={12} /> Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
